import { CommonModule } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy, computed, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { GsAnchoredPanelDirective } from '../../../../directives/anchored-panel.directive';
import { GsIconComponent } from '../../../gs-icon/gs-icon.component';
import { GsFormFieldText } from '../../interfaces/gs-form-schema';
import { GsBaseFormInputComponent } from '../gs-base-input.component';

/** Counter for unique suggestion list ids */
let nextSuggestionsId = 0;

@Component({
  selector: 'gs-text-form',
  imports: [CommonModule, GsAnchoredPanelDirective, GsIconComponent, ReactiveFormsModule, TranslatePipe],
  templateUrl: './gs-text.component.html',
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: GsTextFormComponent
    }
  ]
})
export class GsTextFormComponent extends GsBaseFormInputComponent<string, GsFormFieldText> implements OnInit, ControlValueAccessor {
  // Properties & Signals
  /** ID linking the input to its suggestion list */
  protected readonly suggestionsId = `gs-text-suggestions-${nextSuggestionsId++}`;
  /** Whether the suggestion list is open */
  protected readonly suggestionsOpen = signal(false);
  /** Index of the suggestion highlighted by arrow keys or mouse; -1 if none */
  protected readonly activeIndex = signal(-1);
  /** Suggestions containing the typed text; all of them until the user types */
  protected readonly filteredSuggestions = computed(() => {
    const term = this.filter().trim().toLowerCase();
    return (this.schema().suggestions ?? []).filter((m) => m.toLowerCase().includes(term));
  });
  /** Whether the suggestion list is visible; never without suggestions or matches */
  protected readonly listOpen = computed(() => this.suggestionsOpen() && this.filteredSuggestions().length > 0);
  /** Text the suggestions are filtered by */
  private readonly filter = signal('');

  // Methods
  override writeValue(val: string | number | undefined | null): void {
    val = typeof val === 'number' ? val.toString() : val;
    super.writeValue(val);
  }

  /**
   * Opens the suggestion list with all suggestions, unless it is open already or the field is disabled.
   */
  protected openSuggestions(): void {
    if (this.suggestionsOpen() || this.internalControl().disabled) return;
    this.filter.set('');
    this.activeIndex.set(-1);
    this.suggestionsOpen.set(true);
  }

  /**
   * Closes the suggestion list.
   */
  protected closeSuggestions(): void {
    this.suggestionsOpen.set(false);
  }

  /**
   * Filters the suggestions by the typed text.
   * @param event The input event of the field
   */
  protected onInput(event: Event): void {
    this.filter.set((event.target as HTMLInputElement).value);
    this.activeIndex.set(-1);
    this.suggestionsOpen.set(true);
  }

  /**
   * Moves through the suggestions with the arrow keys, takes the highlighted one with Enter and closes the list with
   * Escape without closing a surrounding dialog.
   * @param event The keydown event of the field
   */
  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      this.openSuggestions();
      const count = this.filteredSuggestions().length;
      if (count === 0) return;
      event.preventDefault();
      const current = this.activeIndex();
      this.activeIndex.set((event.key === 'ArrowDown' ? current + 1 : (current <= 0 ? count : current) - 1) % count);
      return;
    }
    // Without a visible list Enter submits and Escape reaches a surrounding dialog
    if (!this.listOpen()) return;
    const active = this.filteredSuggestions()[this.activeIndex()];
    if (event.key === 'Enter' && active !== undefined) {
      event.preventDefault();
      this.selectSuggestion(active);
    } else if (event.key === 'Escape') {
      event.stopPropagation();
      this.closeSuggestions();
    }
  }

  /**
   * Takes a suggestion as value and closes the list.
   * @param suggestion The chosen suggestion
   */
  protected selectSuggestion(suggestion: string): void {
    this.internalControl().setValue(suggestion);
    this.closeSuggestions();
  }
}
